import {useCallback} from 'react'
import {useSearchParams} from 'react-router-dom'
import {URL_PARAMS} from './urlParams'

export interface FilterState {
  search?: string
  sort?: string
  sortOrder?: 'asc' | 'desc'
  program?: string
  programs?: string // Landing Page: comma-separated program IDs for teams filter
  team?: string
  status?: string
}

export interface UseUrlFiltersReturn {
  filters: FilterState
  setFilter: (key: keyof FilterState, value: string | undefined) => void
  setFilters: (filters: Partial<FilterState>) => void
  clearFilters: () => void
  clearPagination: () => void
}

/**
 * Custom hook for managing filter state synchronized with URL
 * Enables bookmarkable and shareable dashboard views with preserved filter selections
 *
 * @example
 * const {filters, setFilter, setFilters, clearFilters} = useUrlFilters()
 *

 * // Set single filter
 * setFilter('search', 'typescript')
 *
 * // Set multiple filters at once
 * setFilters({search: 'typescript', sort: 'date', sortOrder: 'desc'})
 *
 * // Clear all filters
 * clearFilters()
 */
const FILTER_PARAM_MAP: Record<keyof FilterState, string> = {
  search: URL_PARAMS.SEARCH,
  sort: URL_PARAMS.SORT,
  sortOrder: URL_PARAMS.SORT_ORDER,
  program: URL_PARAMS.PROGRAM_FILTER,
  programs: URL_PARAMS.PROGRAMS,
  team: URL_PARAMS.TEAM_FILTER,
  status: URL_PARAMS.STATUS_FILTER,
}

const updateFilterParam = (params: URLSearchParams, key: keyof FilterState, value: string | undefined) => {
  const paramName = FILTER_PARAM_MAP[key]
  if (value) {
    params.set(paramName, value)
  } else {
    params.delete(paramName)
  }
}

export const useUrlFilters = (): UseUrlFiltersReturn => {
  const [searchParams, setSearchParams] = useSearchParams()

  // Read current filters from URL
  const filters: FilterState = {
    search: searchParams.get(URL_PARAMS.SEARCH) || undefined,
    sort: searchParams.get(URL_PARAMS.SORT) || undefined,
    sortOrder: (searchParams.get(URL_PARAMS.SORT_ORDER) as 'asc' | 'desc') || undefined,
    program: searchParams.get(URL_PARAMS.PROGRAM_FILTER) || undefined,
    programs: searchParams.get(URL_PARAMS.PROGRAMS) || undefined,
    team: searchParams.get(URL_PARAMS.TEAM_FILTER) || undefined,
    status: searchParams.get(URL_PARAMS.STATUS_FILTER) || undefined,
  }

  // Set a single filter and reset pagination atomically
  const setFilter = useCallback(
    (key: keyof FilterState, value: string | undefined) => {
      setSearchParams(
        params => {
          const newParams = new URLSearchParams(params)
          updateFilterParam(newParams, key, value)
          newParams.delete(URL_PARAMS.PAGE)
          newParams.delete(URL_PARAMS.PROGRAM_PAGE)
          return newParams
        },
        {replace: true},
      )
    },
    [setSearchParams],
  )

  // Set multiple filters at once
  const setFilters = useCallback(
    (newFilters: Partial<FilterState>) => {
      setSearchParams(
        params => {
          const newParams = new URLSearchParams(params)
          Object.entries(newFilters).forEach(([key, value]) => {
            updateFilterParam(newParams, key as keyof FilterState, value)
          })
          return newParams
        },
        {replace: true},
      )
    },
    [setSearchParams],
  )

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSearchParams(
      params => {
        const newParams = new URLSearchParams(params)

        // Remove all filter params
        newParams.delete(URL_PARAMS.SEARCH)
        newParams.delete(URL_PARAMS.SORT)
        newParams.delete(URL_PARAMS.SORT_ORDER)
        newParams.delete(URL_PARAMS.PROGRAM_FILTER)
        newParams.delete(URL_PARAMS.PROGRAMS)
        newParams.delete(URL_PARAMS.TEAM_FILTER)
        newParams.delete(URL_PARAMS.STATUS_FILTER)

        // Reset pagination when filters are cleared
        newParams.delete(URL_PARAMS.PAGE)
        newParams.delete(URL_PARAMS.PROGRAM_PAGE)

        return newParams
      },
      {replace: true},
    )
  }, [setSearchParams])

  // Clear pagination only (useful when filters change)
  const clearPagination = useCallback(() => {
    setSearchParams(
      params => {
        const newParams = new URLSearchParams(params)
        newParams.delete(URL_PARAMS.PAGE)
        newParams.delete(URL_PARAMS.PROGRAM_PAGE)
        return newParams
      },
      {replace: true},
    )
  }, [setSearchParams])

  return {
    filters,
    setFilter,
    setFilters,
    clearFilters,
    clearPagination,
  }
}
